import { useEffect, useState } from "react";
import { useStore } from "../hooks.ts";

interface State {
  link: { peer: string; createdAt: number; lastUsedAt: number | null } | null;
  code: { expiresAt: number } | null;
}

const when = (at: number) => new Date(at).toLocaleString("de-DE", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
const time = (at: number) => new Date(at).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });

/**
 * Konto → SpeisePlan (docs/18-vorratsbuch.md, V2): VorratsBuch makes a code, SpeisePlan redeems it
 * in its settings; from then on SpeisePlan sees the stock per base product. Owners and managers.
 */
export function SpeiseplanLink() {
  const { store } = useStore();
  const [state, setState] = useState<State | null>(null);
  const [code, setCode] = useState<{ code: string; expiresAt: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fail = (e: unknown) => setError(e instanceof Error && !/fetch|abort/i.test(e.message) ? e.message : "Ohne Verbindung zum Server geht das nicht.");
  const load = () =>
    store
      .request<State>("/api/workspace/speiseplan")
      .then((s) => [setState(s), setError(null)])
      .catch(fail);
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function make() {
    try {
      setCode(await store.request<{ code: string; expiresAt: number }>("/api/workspace/speiseplan/code", { method: "POST", body: "{}" }));
      setError(null);
    } catch (e) {
      fail(e);
    }
  }

  async function unlink() {
    if (!confirm("Kopplung mit SpeisePlan lösen? SpeisePlan sieht den Vorrat dann nicht mehr, bis ihr neu koppelt.")) return;
    try {
      await store.request("/api/workspace/speiseplan", { method: "DELETE" });
      setCode(null);
      await load();
    } catch (e) {
      fail(e);
    }
  }

  return (
    <section className="card" data-testid="speiseplan">
      <h2>SpeisePlan</h2>
      <p className="muted">Gekoppelt sieht SpeisePlan, was im Vorrat ist: „komplett aus dem Vorrat“ und „fehlt: Sahne“ an jedem Rezept, was bald abläuft zuerst. SpeisePlan sieht nur Mengen und MHD, keine Preise.</p>
      {state?.link ? (
        <p data-testid="speiseplan-linked">
          Gekoppelt mit SpeisePlan-Haushalt <strong>{state.link.peer}</strong> seit {when(state.link.createdAt)}
          <br />
          <span className="muted">{state.link.lastUsedAt ? `zuletzt abgefragt ${when(state.link.lastUsedAt)}` : "noch nicht abgefragt"}</span>
        </p>
      ) : (
        state && <p className="muted">Noch nicht gekoppelt.</p>
      )}
      {code && code.expiresAt > Date.now() && (
        <div className="notice" data-testid="speiseplan-code-box">
          Code für SpeisePlan: <strong data-testid="speiseplan-code">{code.code}</strong>
          <br />
          In SpeisePlan unter Einstellungen → VorratsBuch eingeben. Gilt einmal, bis {time(code.expiresAt)} Uhr.
        </div>
      )}
      <div className="buttons">
        <button onClick={() => void make()} data-testid="speiseplan-make-code">
          {state?.link ? "Neu koppeln (Code)" : "Code für SpeisePlan erzeugen"}
        </button>
        {state?.link && (
          <button className="link danger" onClick={() => void unlink()} data-testid="speiseplan-unlink">
            Kopplung lösen
          </button>
        )}
      </div>
      {state?.link && <p className="hint">Ein neuer Code ersetzt die bestehende Kopplung, sobald SpeisePlan ihn einlöst.</p>}
      {error && <p className="error-box">{error}</p>}
    </section>
  );
}
